/* eslint-disable prefer-destructuring */
import { InferGetServerSidePropsType } from 'next';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';

import { TourType } from '@/_models/Tour';
import LoadingProgress from '@/components/common/LoadingProgress';
import { getPageTour } from '@/services/tour';
import { getSlug } from '@/utils/getSlug';

import PageHeader from '../../../components/common/PageHeader';

const RegularTour = dynamic(() => import('../../../components/RegularTour'), {
  ssr: false,
});

const getParams = (slug: string | string[]) => {
  console.log('🔍 [getParams] Starting URL parameter parsing', {
    slug,
    slugType: typeof slug,
    slugLength: Array.isArray(slug) ? slug.length : 1,
  });

  // renderType
  // 0 : /tour/:slug
  // 1 : /token/:token
  // 2 : /tour/:slug/:videoType/:rule/:agentId/:chatToken
  // 2 : /tour/:slug/:videoType/:rule/:chatToken
  let matterId;
  let videoType;
  let rule;
  let agentId;
  let room;
  let renderType = 2;

  if (
    slug?.length === 1 ||
    (slug.length > 1 && slug[1] === 'processOrder') ||
    (slug.length > 1 && slug[1] === 'book') ||
    (slug.length > 1 && slug[1] === 'login')
  ) {
    matterId = slug[0];
    renderType = 0;
    console.log('📍 [getParams] Simple tour or special action detected', {
      matterId,
      renderType,
      action: slug.length > 1 ? slug[1] : 'none',
    });
  } else if (slug?.length === 4) {
    matterId = slug[0];
    videoType = slug[1];
    rule = slug[2];
    room = slug[3];
    console.log('📍 [getParams] Video tour (4 params) detected', {
      matterId,
      videoType,
      rule,
      room,
      renderType,
    });
  } else if (slug?.length === 5) {
    matterId = slug[0];
    videoType = slug[1];
    rule = slug[2];
    agentId = slug[3];
    room = slug[4];
    console.log('📍 [getParams] Video tour with agent (5 params) detected', {
      matterId,
      videoType,
      rule,
      agentId,
      room,
      renderType,
    });
  } else {
    console.warn('⚠️ [getParams] Invalid URL format detected', {
      slug,
      slugLength: Array.isArray(slug) ? slug.length : 1,
    });
  }
  const params = {
    matterId,
    videoType,
    rule,
    agentId,
    room,
    renderType,
  };

  console.log('✅ [getParams] URL parameters parsed successfully', params);
  return params;
};

export const getServerSideProps = async (context: {
  query: { slug: any; pmeToken: any };
  req: any;
}) => {
  console.log('🚀 [getServerSideProps] Tour page request started', {
    rawSlug: context.query.slug,
    pmeToken: context.query.pmeToken ? '[PRESENT]' : '[ABSENT]',
    host: context.req.headers.host,
    userAgent: context.req.headers['user-agent'],
  });

  try {
    const slug = getSlug(context.query.slug);
    console.log('🔄 [getServerSideProps] Slug processed', { processedSlug: slug });

    // Parse URL parameters
    const urlParams = getParams(slug);

    const tourRequestParams = {
      slug,
      mode: 'regular',
      pmeToken: context.query.pmeToken,
      origin: context.req.headers.host,
    };

    console.log('📡 [getServerSideProps] Fetching tour data', {
      ...tourRequestParams,
      pmeToken: tourRequestParams.pmeToken ? '[PRESENT]' : '[ABSENT]',
      urlParams,
    });

    const pageInfo = await getPageTour(tourRequestParams);

    if (!pageInfo) {
      console.warn('❌ [getServerSideProps] Tour data not found', {
        slug,
        origin: context.req.headers.host,
      });
      return {
        notFound: true,
      };
    }

    console.log('✅ [getServerSideProps] Tour data retrieved successfully', {
      slug,
      tourTitle: pageInfo.title || '[NO_TITLE]',
      hasImage: !!pageInfo.tourImage,
      hasTourData: !!(pageInfo as any).tourData,
      dataKeys: Object.keys(pageInfo),
    });

    const data = JSON.stringify(pageInfo);
    console.log('🎯 [getServerSideProps] Tour page request completed successfully', {
      slug,
      dataSize: data.length,
    });

    return {
      props: {
        data,
      },
    };
  } catch (error) {
    console.error('💥 [getServerSideProps] Tour page request failed', {
      error:
        error instanceof Error
          ? {
              message: error.message,
              stack: error.stack,
              name: error.name,
            }
          : error,
      slug: context.query.slug,
    });

    // Return error page or redirect
    return {
      props: {
        data: JSON.stringify({
          error: 'Failed to load tour data',
          title: 'Error Loading Tour',
          description: 'Unable to load the requested tour.',
        }),
      },
    };
  }
};

const IndexPage = ({ data }: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  console.log('🎨 [IndexPage] Component mounting', { dataSize: data.length });

  const parsedData = JSON.parse(data);
  console.log('📊 [IndexPage] Data parsed', {
    dataKeys: Object.keys(parsedData),
    hasError: !!parsedData.error,
    title: parsedData.title || '[NO_TITLE]',
  });

  const {
    title,
    description,
    tourImage,
    website,
    fbPixel,
    fbAppId,
    faviconUrl,
    htmlCode,
    tourData,
    tags,
    matterTags,
    charset,
    error,
  } = parsedData;

  const router = useRouter();
  console.log('🧭 [IndexPage] Router query', { routerSlug: router.query.slug });

  const { matterId, renderType, videoType, rule, agentId, room } = getParams(router.query.slug);

  console.log('🎯 [IndexPage] Component state', {
    matterId,
    renderType,
    videoType,
    rule,
    agentId,
    room,
    hasError: !!error,
    hasTourData: !!tourData,
    hasImage: !!tourImage,
    tagsCount: tags?.length || 0,
    matterTagsCount: matterTags?.length || 0,
  });

  // Handle error state
  if (error) {
    console.error('🚨 [IndexPage] Rendering error page', { error, slug: router?.query?.slug });
  }

  console.log('🎬 [IndexPage] Rendering tour page', {
    matterId,
    renderType,
    hasVideoType: !!videoType,
    hasAgent: !!agentId,
    hasRoom: !!room,
    environment: {
      cdn: !!process.env.NEXT_PUBLIC_CDN,
      api: !!process.env.NEXT_PUBLIC_API,
      matterportSdk: !!process.env.NEXT_PUBLIC_MP_SDK_KEY,
      paypalClient: !!process.env.NEXT_PUBLIC_PAYPAL_CLIENT,
    },
  });

  return (
    <PageHeader
      title={title}
      description={description}
      slug={matterId}
      tourImage={tourImage}
      website={website}
      fbPixel={fbPixel}
      fbAppId={fbAppId}
      faviconUrl={faviconUrl}
      htmlCode={htmlCode}
      type="tour"
      charset={charset}
    >
      <RegularTour
        tourData={tourData}
        tags={tags}
        matterTags={matterTags}
        tourImage={tourImage}
        renderType={renderType}
        matterId={matterId}
        videoType={videoType}
        agentId={agentId || null}
        rule={rule}
        room={room}
        reactAws={process.env.NEXT_PUBLIC_CDN}
        reactAppDomain={process.env.NEXT_PUBLIC_API}
        reactAppMatterPort={process.env.NEXT_PUBLIC_MP_SDK_KEY}
        reactPaypalClient={process.env.NEXT_PUBLIC_PAYPAL_CLIENT}
      />
    </PageHeader>
  );
};

export default IndexPage;
